<template>
  <div v-if="headerStore.isShowFormalStatement && messageArray.length > 0" class="fixed inset-0 bg-black/50 flex justify-center items-center z-50" @click.self="selfClosePopup">
    <div class="bg-white rounded-xl w-[90%] sm:w-[80%] md:w-[70%] lg:w-[60%] xl:max-w-[1200px] lg:max-h-[75vh] md:max-h-[65vh] max-h-[55vh] sm:max-h-[50vh] overflow-y-auto shadow-lg animate-popupFadeIn popup-container-scroll">
      <div class="sticky top-0 bg-white z-50 flex justify-between items-center px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-200">
        <h3 class="flex-1 text-center text-[clamp(18px,6vw,48px)] sm:text-[clamp(16px,4vw,44px)] font-semibold text-gray-800 m-0" :style="{ fontFamily: titleFontFamily }">
          {{ title }}
        </h3>
        <button v-if="showCloseButton" class="bg-transparent border-none text-1xl sm:text-2xl cursor-pointer text-gray-500 transition-colors duration-200 mr-2 sm:mr-5 hover:text-gray-800" @click="closePopup">✕</button>
      </div>

      <div class="my-2.5 p-4 sm:p-5 px-[clamp(13px,3vw,50px)] sm:px-[clamp(17px,4.5vw,50px)]">
        <div v-for="(text, index) in messageArray" :key="index" class="mb-2.5 text-[clamp(10px,3vw,20px)] sm:text-[clamp(8px,2vw,17px)] text-gray-500 leading-relaxed text-justify" :style="{ fontFamily: contentFontFamily }" v-html="text"></div>
        <div class="mt-5 mb-2.5 text-[clamp(10px,3vw,20px)] sm:text-[clamp(8px,2vw,17px)] text-gray-500 leading-relaxed text-justify" :style="{ fontFamily: contentFontFamily }" v-html="footer"></div>
      </div>

      <div class="sticky bottom-0 bg-white flex justify-end px-3 sm:px-1 py-3 sm:py-4 border-t border-gray-200 gap-3 sm:gap-5 mr-4 sm:mr-8 pb-4 sm:pb-8">
        <button
          v-if="showDismissButton"
          class="bg-amber-800 text-white border-none px-3 py-2 rounded-md cursor-pointer text-[clamp(10px,3vw,20px)] sm:text-[clamp(8px,2vw,17px)] font-medium transition-colors duration-200 hover:bg-amber-900"
          :style="{ fontFamily: buttonFontFamily }"
          @click="closePopup"
        >
          {{ t("common.notedAndDismiss") }}
        </button>
        <button
          v-if="showConfirmButton"
          class="bg-amber-800 text-white border-none px-3 py-2 rounded-md cursor-pointer text-[clamp(10px,3vw,20px)] sm:text-[clamp(8px,2vw,17px)] font-medium transition-colors duration-200 hover:bg-amber-900"
          :style="{ fontFamily: buttonFontFamily }"
          @click="confirmAction"
        >
          {{ t("common.confirm") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useHeaderStore } from "@/stores/header";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
  title?: string;
  message?: string | string[];
  footer?: string;
  showDismissButton?: boolean;
  showCloseButton?: boolean;
  showConfirmButton?: boolean;
  canCloseOnOverlayClick?: boolean;
  titleFontFamily?: string;
  contentFontFamily?: string;
  buttonFontFamily?: string;
}

const emit = defineEmits<{
  (e: "confirm"): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  title: "聲明",
  message: () => [],
  footer: "",
  showCloseButton: false,
  showConfirmButton: false,
  canCloseOnOverlayClick: false,
  showDismissButton: false,
  titleFontFamily: "'Noto Sans TC', 'Microsoft JhengHei', 'PingFang TC', 'Helvetica Neue', Arial, sans-serif",
  contentFontFamily: "'Noto Sans TC', 'Microsoft JhengHei', 'PingFang TC', 'Helvetica Neue', Arial, sans-serif",
  buttonFontFamily: "'Noto Sans TC', 'Microsoft JhengHei', 'PingFang TC', 'Helvetica Neue', Arial, sans-serif",
});

const confirmAction = () => {
  emit("confirm");
};

const headerStore = useHeaderStore();

const messageArray = computed(() => {
  if (Array.isArray(props.message)) {
    return props.message;
  } else if (typeof props.message === "string" && props.message) {
    return [props.message];
  }
  return [];
});

const selfClosePopup = () => {
  if (props.canCloseOnOverlayClick) {
    headerStore.isShowFormalStatement = false;
  }
};

const closePopup = () => {
  headerStore.isShowFormalStatement = false;
};
</script>

<style scoped>
@keyframes popupFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-popupFadeIn {
  animation: popupFadeIn 0.3s ease-out;
}

.popup-container-scroll::-webkit-scrollbar {
  width: 8px;
}

.popup-container-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.popup-container-scroll::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.popup-container-scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 粗體樣式 */
:deep(b) {
  font-weight: 700;
  color: #333;
}

:deep(strong) {
  font-weight: 700;
  color: #333;
}
</style>